import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

// Default theme from CLAUDE.md
const DEFAULT_THEME = {
  primary_color: '#072040',
  secondary_color: '#f0a500',
  logo_url: null,
  logo_height: null,
  company_name: 'Drummond Portal',
};

// Try to load cached theme from localStorage to prevent flash
const getCachedTheme = () => {
  try {
    const cached = localStorage.getItem('portal_theme');
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return DEFAULT_THEME;
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [theme, setTheme] = useState(getCachedTheme);

  // Update theme when user changes (branding comes from user object)
  useEffect(() => {
    if (user?.branding) {
      const newTheme = {
        primary_color: user.branding.primary_color || DEFAULT_THEME.primary_color,
        secondary_color: user.branding.secondary_color || DEFAULT_THEME.secondary_color,
        logo_url: user.branding.logo_url,
        logo_height: user.branding.logo_height ?? null,
        company_name: user.branding.company_name || DEFAULT_THEME.company_name,
      };
      setTheme(newTheme);
      // Cache theme to prevent flash on next load
      localStorage.setItem('portal_theme', JSON.stringify(newTheme));
    } else if (user === null) {
      // User logged out - clear cached theme
      setTheme(DEFAULT_THEME);
      localStorage.removeItem('portal_theme');
    }
    // Don't reset theme while user is loading (undefined) to prevent flash
  }, [user]);

  // Apply theme to CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--primary-color', theme.primary_color);
    document.documentElement.style.setProperty('--secondary-color', theme.secondary_color);
  }, [theme]);

  const value = {
    theme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
